/**
 * HearthTag —— 炉火状态 chip 单一映射 (DESIGN-LANGUAGE.md §7)
 * -----------------------------------------------------------------------------
 * 一个映射函数终结全 admin 的状态色。**严禁** `<Tag color="blue|cyan|gold|green|...">`
 * (AntD 预设是冷调 + 不过对比度)。状态文字一律 600 文字档 (RL-8),底用对应浅底,
 * 方角 xs 圆角 6px (状态 tag 非胶囊)。
 *
 * 语义 → 色 (§7 表):
 *   info     进行中/信息/身份角色/进度    黛蓝 #2E6094 / #E9F1F9   (Ricky 2026-06-23: 职位/角色/进度走信息档)
 *   success  完成/成功/通过/正常/生效      盈绿 #237A46 / #E8F5EC
 *   danger   驳回/危险/缺勤/超标/退回      椒红 #B93A33 / #FBEAE8
 *   warning  临期/整改/待处理/平/风险      琥珀 #956102 / #FCF3DF
 *   neutral  待办/中性pending/草稿/属性    ink-2 #5A4F42 / paper-2 #F4EEE6
 *   muted    作废/禁用/空                  ink-4 #A39684 / paper-2 #F4EEE6
 */
import { Tag } from 'antd';
import type { TagProps } from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import { hearth } from '../theme/antd-theme';

export type HearthTagSemantic =
  | 'info'
  | 'success'
  | 'danger'
  | 'warning'
  | 'neutral'
  | 'muted';

export function hearthTagColor(semantic: HearthTagSemantic): { fg: string; bg: string } {
  switch (semantic) {
    case 'info':
      return { fg: hearth.infoText, bg: hearth.infoBg }; // #2E6094 / #E9F1F9
    case 'success':
      return { fg: hearth.upText, bg: hearth.upBg }; // #237A46 / #E8F5EC
    case 'danger':
      return { fg: hearth.downText, bg: hearth.downBg }; // #B93A33 / #FBEAE8
    case 'warning':
      return { fg: hearth.warnText, bg: hearth.warnBg }; // #956102 / #FCF3DF
    case 'muted':
      return { fg: hearth.ink4, bg: hearth.paper2 }; // #A39684 / #F4EEE6
    case 'neutral':
    default:
      return { fg: hearth.ink2, bg: hearth.paper2 }; // #5A4F42 / #F4EEE6
  }
}

export interface HearthTagProps extends Omit<TagProps, 'color' | 'children'> {
  semantic: HearthTagSemantic;
  children?: ReactNode;
  style?: CSSProperties;
}

/** 炉火状态 chip —— 用 semantic 取代 AntD 预设 color (§7) */
export function HearthTag({ semantic, style, children, ...rest }: HearthTagProps) {
  const { fg, bg } = hearthTagColor(semantic);
  return (
    <Tag
      bordered={false}
      {...rest}
      style={{
        color: fg,
        background: bg,
        borderRadius: 6, // 状态 tag 方角 xs (§7)
        fontWeight: 600, // 600 文字档 (RL-8)
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}
